import React, { useContext, useEffect, useState } from 'react';

import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { getSailing } from '@go-client/lib/api/itinerary';
import {
  populate as populatePlan,
  getPlan,
  createContent,
  updateContent,
  deleteContent,
  updateEvent,
  createEvent,
  deleteEvent,
} from '@go-client/lib/api/plan';
import { attemptCalcAdjacentSailingDay } from '@go-client/lib/sailing';
import { lastEl } from '@go-shared/lib/functional';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { RouteComponentProps } from 'react-router-dom';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { ExportProgramParams, FeedEditor, FeedEditorInfo } from './FeedEditor';
import { exportFeed } from '@go-client/lib/api/feedTemplate';
import { Alert, Anchor } from '@spark-labs/avocado-ui';
import { FeedPage } from '@go-shared/types/feedTemplate';
import { updateFeedEditor } from '@go-client/lib/api/contentTemplate';
import { resolveFeedPath, resolvePlannerPath } from '@go-client/routes';
import { BareContent } from '@go-shared/types/content';
import { BareEvent } from '@go-shared/types/event';
import { getGroupEvents } from '@go-client/lib/api/groupEvent';
import { makeSailingDayBundle } from '@go-shared/lib/sailing';
import { populate, populateDay } from '@go-shared/lib/itinerary';

interface SailingFeedEditorParams {
  sailing: string;
  sailingDay: string;
}

type SailingFeedEditorProps = RouteComponentProps<SailingFeedEditorParams>;

export default function SailingFeedEditor(props: SailingFeedEditorProps) {
  const { activeProperty: property, refreshContext: refreshActiveProperty } = useActiveProperty();
  const { defaultLanguage, languages, plugins } = useServerInfo();
  const { settingsPanel, setSettingsPanel } = useContext(FeedEditorCtx);
  const { user } = useUserInfo();
  const [editorInfo, setEditorInfo] = useState<FeedEditorInfo | undefined>(undefined);

  const { sailing: sailingId, sailingDay: sailingDayId } = props.match.params;

  async function loadEditorInfo(skipTemplates?: boolean) {
    const bareSailing = await getSailing(sailingId);

    if (!bareSailing) {
      throw new Error(`Could not find sailing ${sailingId}`);
    }

    const bareSailingDayIdx = bareSailing.days.findIndex(d => d._id === sailingDayId);

    if (bareSailingDayIdx < 0) {
      throw new Error(`Could not find sailing day ${sailingDayId}`);
    }

    const bareSailingDay = bareSailing.days[bareSailingDayIdx];
    const barePlan = await getPlan(bareSailingDay.plan);
    const plan = populatePlan(barePlan, property);

    // calculate link to previous day and to next day so that the daily planner
    // can render a chevron to go forward or backwards within the canvas
    const adjacentSailingBundles = await attemptCalcAdjacentSailingDay(
      bareSailing,
      bareSailingDayIdx
    );

    const nextSailingBundle = adjacentSailingBundles
      ? adjacentSailingBundles.nextSailingBundle
      : undefined;

    const prevSailingBundle = adjacentSailingBundles
      ? adjacentSailingBundles.prevSailingBundle
      : undefined;

    const nextFeedDayLink = nextSailingBundle
      ? resolveFeedPath(
          nextSailingBundle.sailing.property,
          nextSailingBundle.sailing._id,
          nextSailingBundle.sailingDay._id
        )
      : undefined;

    const prevFeedDayLink = prevSailingBundle
      ? resolveFeedPath(
          prevSailingBundle.sailing.property,
          prevSailingBundle.sailing._id,
          prevSailingBundle.sailingDay._id
        )
      : undefined;

    const currPlannerLink = resolvePlannerPath(
      plan.property._id,
      bareSailing._id,
      bareSailingDay._id
    );

    const bareGroupEvents = await getGroupEvents(
      plan.property._id,
      bareSailingDay.date,
      bareSailingDay.date
    );

    const dayBundle = makeSailingDayBundle(
      populate(bareSailing, property, [], []),
      populateDay(bareSailingDay, [])
    );

    setEditorInfo({
      plan,
      groupEvts: bareGroupEvents,
      feeds: bareSailingDay.feeds,
      dayBundle,
      nextFeedDayLink,
      prevFeedDayLink,
      pathToPlanner: currPlannerLink,
      skipTemplates,
    });
  }

  useEffect(() => {
    loadEditorInfo().catch(console.error);
  }, [sailingId, sailingDayId]);

  async function handleExportProgram(params: ExportProgramParams, feedSlot: number) {
    const dayBundle = editorInfo?.dayBundle;

    if (!dayBundle) return;

    let links = await exportFeed(
      dayBundle.sailing._id,
      dayBundle.day._id,
      {
        legacy: false,
        lang: params.language,
        hideBackground: params.hideBackground,
      },
      feedSlot
    );

    await Alert.success({
      htmlContent: (
        <div className="pt-2 pb-4">
          {links.map((lnk, i) => (
            <div>
              <Anchor target="_blank" href={lnk}>
                {/* FIXME: hack, assumes pdf is first and htmls follow */}
                Download {lastEl(lnk.split('.')).toUpperCase()} {i !== 0 ? `${i}` : ''}
              </Anchor>
            </div>
          ))}
        </div>
      ),
    });
  }

  async function handleUpdateProgram(currentPage: FeedPage, feedSlot: number) {
    const dayBundle = editorInfo?.dayBundle;

    if (!dayBundle) return;

    await updateFeedEditor(
      property,
      dayBundle.sailing._id,
      dayBundle.day._id,
      currentPage,
      feedSlot
    );
  }

  const showDailyProgramFeedOptions = !!plugins.find(p => p.name === 'ddp');

  async function handleOnCreateContent(content: BareContent) {
    if (editorInfo) {
      return createContent(editorInfo.plan._id, content);
    }
  }

  async function handleOnUpdateContent(content: BareContent) {
    if (editorInfo) {
      return updateContent(editorInfo.plan._id, content);
    }
  }

  async function handleOnDeleteContent(contentId: string) {
    if (editorInfo) {
      return deleteContent(editorInfo.plan._id, contentId);
    }
  }

  async function handleOnUpdateEvent(
    event: BareEvent,
    applyStaffToPlan?: boolean,
    key?: string,
    transferToTargetDay?: string
  ) {
    if (editorInfo) {
      return updateEvent(editorInfo.plan._id, event, key, applyStaffToPlan, transferToTargetDay);
    }
  }

  async function handleOnCreateEvent(event: BareEvent, applyStaffToPlan?: boolean) {
    if (!editorInfo) throw new Error('Editor info not loaded');

    return createEvent(editorInfo.plan._id, event, applyStaffToPlan);
  }

  async function handleOnDeleteEvent(eventId: string) {
    if (editorInfo) {
      return deleteEvent(editorInfo.plan._id, eventId);
    }
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <FeedEditor
        {...props}
        variant="navy"
        user={user}
        property={property}
        refreshActiveProperty={refreshActiveProperty}
        defaultLanguage={defaultLanguage}
        languages={languages}
        showDailyProgramFeedOptions={showDailyProgramFeedOptions}
        settingsPanel={settingsPanel}
        setSettingsPanel={setSettingsPanel}
        editorInfo={editorInfo}
        needsRefresh={loadEditorInfo}
        onExportProgram={handleExportProgram}
        onUpdateProgram={handleUpdateProgram}
        onCreateContent={handleOnCreateContent}
        onUpdateContent={handleOnUpdateContent}
        onDeleteContent={handleOnDeleteContent}
        onUpdateEvent={handleOnUpdateEvent}
        onCreateEvent={handleOnCreateEvent}
        onDeleteEvent={handleOnDeleteEvent}
      />
    </DndProvider>
  );
}
